import { useQuery } from "@tanstack/react-query";
import { Modal } from "../ui/Modal";
import { api, type Ticket as TicketType } from "../../lib/api";
import { PriorityBadge } from "./PriorityBadge";
import { StatusBadge } from "./StatusBadge";
import { CategoryBadge } from "./CategoryBadge";
import { SLACountdown } from "./SLACountdown";
import { Avatar } from "../ui/Avatar";
import { fmtDateTime, fmtRelative } from "../../lib/format";
import { GlassCard } from "../ui/GlassCard";
import { Spinner } from "../ui/FullScreenLoader";
import { Image, Paperclip, Lock } from "lucide-react";

export function TicketDetailModal({
  ticketId,
  open,
  onClose,
}: {
  ticketId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: ticket, isLoading, isError } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: async () =>
      (await api.get<{ ticket: TicketType }>(`/api/tickets/${ticketId}`)).data.ticket,
    enabled: !!ticketId && open,
  });

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Ticket Details" size="xl">
      {isLoading ? (
        <div className="py-12 flex justify-center">
          <Spinner />
        </div>
      ) : isError || !ticket ? (
        <div className="py-12 text-center text-red-400">
          Failed to load ticket details. You may not have permission.
        </div>
      ) : (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <PriorityBadge priority={ticket.priority} />
                <StatusBadge status={ticket.status} />
                <CategoryBadge category={ticket.category} />
                <span className="text-xs text-slate-400">
                  #{ticket.id.slice(-8).toUpperCase()}
                </span>
                {ticket.issue && (
                  <>
                    <span className="text-slate-500 text-xs">·</span>
                    <span className="text-xs font-medium text-slate-300">
                      {ticket.issue}
                    </span>
                  </>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 ml-auto">
                  <Lock className="w-3 h-3" /> Read-only
                </span>
              </div>
              <h2 className="text-xl font-semibold text-white text-balance">
                {ticket.title}
              </h2>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                <Avatar name={ticket.submitter?.name || "?"} url={ticket.submitter?.avatarUrl} size="xs" />
                <span>
                  Submitted by{" "}
                  <span className="text-slate-200">{ticket.submitter?.name}</span>
                  {ticket.submitter?.department && (
                    <span className="text-slate-500"> - {ticket.submitter.department}</span>
                  )}
                </span>
                <span>·</span>
                <span>{fmtRelative(ticket.createdAt)}</span>
              </div>
            </div>
            <SLACountdown
              deadline={ticket.slaDeadline}
              priority={ticket.priority}
              status={ticket.status}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <GlassCard>
                <h3 className="text-sm font-semibold text-white mb-2">Description</h3>
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {ticket.description}
                </p>
                
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/5 space-y-3">
                    <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Image className="w-3.5 h-3.5 text-brand-300" />
                      Attachments ({ticket.attachments.length})
                    </h4>
                    <div className="space-y-2">
                      {ticket.attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10 text-sm">
                          <Paperclip className="w-4 h-4 text-slate-400" />
                          <span className="truncate flex-1 text-slate-200">{att.filename}</span>
                          <span className="text-xs text-slate-500">{(att.size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </GlassCard>
            </div>

            <div className="space-y-4">
              <GlassCard>
                <h3 className="text-sm font-semibold text-white mb-3">Info</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-400">Created</dt>
                    <dd className="text-slate-200">{fmtDateTime(ticket.createdAt)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-400">Updated</dt>
                    <dd className="text-slate-200">{fmtDateTime(ticket.updatedAt)}</dd>
                  </div>
                  {ticket.resolvedAt && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-slate-400">Resolved at</dt>
                      <dd className="text-slate-200">{fmtDateTime(ticket.resolvedAt)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-2 border-t border-white/5 pt-2 mt-2">
                    <dt className="text-slate-400">Assignee</dt>
                    <dd className="text-slate-200 text-right flex flex-col items-end gap-0.5">
                      {ticket.assignee ? (
                        <>
                          <span className="inline-flex items-center gap-1.5">
                            <Avatar name={ticket.assignee.name} url={ticket.assignee.avatarUrl} size="xs" />
                            {ticket.assignee.name}
                          </span>
                        </>
                      ) : (
                        <span className="italic text-slate-500">Unassigned</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </GlassCard>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
